import { green, red } from '../../assets/colors'
import { Box, Stack, Typography } from '@mui/material'
import { Fragment, memo, useEffect, useMemo, useRef, useState } from 'react'
import { formatFundamentalPrice, formatLargeNumber } from '../../util/screener'
import { DataType, FieldType, ScreenerField } from '../../proto/generated/screener'
import { makeStyles } from 'tss-react/mui'
import { toLocaleStringIn } from '../../util/general'

export type OverviewValueProps = {
    fieldData: ScreenerField | undefined
    text: string | undefined
    overview: boolean
    hideCurrencySymbol?: boolean
    isCustomColumn?: boolean
}

const useStyles = makeStyles()((theme) => ({
    priceText: {
        color: theme.palette.text.primary,
        fontSize: 'body2',
        display: 'inline'
    },
    currencyText: {
        color: theme.palette.text.disabled,
        fontSize: '10px',
        display: 'inline'
    }
}))

const OverviewValue = ({ fieldData, text, overview, hideCurrencySymbol = false, isCustomColumn = false }: OverviewValueProps) => {
    const { classes, cx } = useStyles()

    const fieldType = useMemo(() => fieldData?.fieldType ?? FieldType.FIELD_TYPE_UNKNOWN, [fieldData])
    const dataType = useMemo(() => fieldData?.dataType ?? DataType.DATA_TYPE_UNKNOWN, [fieldData])
    const precision = useMemo(() => fieldData?.precision ?? 0, [fieldData])
    const abbreviate = useMemo(() => {
        return fieldData?.abbreviate ?? (isCustomColumn ? true : false)
    }, [fieldData, isCustomColumn])
    const textRef = useRef<HTMLDivElement>(null);
    const [isOverflowed, setIsOverflowed] = useState(false);

    useEffect(() => {
        const el = textRef.current;
        if (el) {
            setIsOverflowed(el.scrollWidth > el.clientWidth);
        }
    }, [text]);

    const modifiedText = useMemo(() => {
        if (dataType === DataType.DOUBLE || dataType === DataType.BIGDECIMAL || isCustomColumn) {
            return Number(text).toFixed(precision)
        }
        return text
    }, [dataType, precision, text, fieldData, isCustomColumn])

    if (!text) {
        return <span>-</span>
    }

    const renderCurrency = (value: string | number, currencySymbol = 'INR') => (
        <>
            <Typography className={cx(classes.priceText, 'font-feature-num')} variant="body2">
                {value}
            </Typography>
            {hideCurrencySymbol && (
                <Typography className={cx(classes.currencyText, 'font-feature-num')}>
                    {currencySymbol}
                </Typography>
            )}
        </>
    )

    const isNumericField =
        fieldType === FieldType.NUMBER ||
        fieldType === FieldType.FINANCIAL_VALUE ||
        dataType === DataType.DOUBLE ||
        dataType === DataType.BIGDECIMAL ||
        dataType === DataType.INTEGER ||
        dataType === DataType.LONG

    const numericTextSx = {
        width: '100%',
        textAlign: overview ? 'left' : 'right',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    } as const
    const textSx = {
        width: '100%',
        textAlign: 'left',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    } as const

    const renderValue = () => {
        if (isCustomColumn) {
            return (
                <Stack ref={textRef} title={isOverflowed ? modifiedText : undefined} gap={0.5} justifyContent={overview ? 'start' : 'end'} alignItems="baseline" direction="row-reverse">
                    {renderCurrency(abbreviate ? formatFundamentalPrice(Number(modifiedText)) : toLocaleStringIn(Number(modifiedText)))}
                </Stack>
            )
        }

        switch (fieldType) {
            case FieldType.FUNDAMENTAL_PRICE:
                return (Number(modifiedText)).toFixed(0) === '0' ? (
                    <span>-</span>
                ) : (
                    <Box
                        ref={textRef}
                        title={isOverflowed ? modifiedText : undefined}
                    >
                        <Stack gap={0.5} justifyContent={overview ? 'start' : 'end'} alignItems="baseline"
                            direction="row-reverse">
                            {renderCurrency(formatFundamentalPrice(Number(modifiedText)), '₹')}
                        </Stack>
                    </Box>
                )
            case FieldType.PRICE:
                return (
                    <Stack ref={textRef} title={isOverflowed ? modifiedText : undefined} gap={0.5} justifyContent={overview ? 'start' : 'end'} alignItems="baseline" direction="row-reverse">
                        {renderCurrency(abbreviate ? formatLargeNumber(Number(modifiedText)) : toLocaleStringIn(Number(modifiedText)), '₹')}
                    </Stack>
                )
            case FieldType.PERCENT_CHANGE:
                return (
                    <span
                        ref={textRef}
                        title={isOverflowed ? modifiedText : undefined}
                        className={`${overview ? '' : Number(modifiedText) > 0 ? 'green-text' : Number(modifiedText) < 0 ? 'red-text' : ''} font-feature-num`}
                        style={overview ? {
                            color: Number(modifiedText) > 0 ? green : Number(modifiedText) < 0 ? red : undefined,
                        } : undefined}
                    >
                        {modifiedText}<span>%</span>
                    </span>
                )
            case FieldType.PERCENT:
                return <span className={'font-feature-num'} ref={textRef} title={isOverflowed ? modifiedText : undefined}>{modifiedText}<span>%</span></span>
            case FieldType.FINANCIAL_VALUE: {
                const n = Number(modifiedText)
                if (!Number.isFinite(n) || n === 0) {
                    return <span>-</span>
                }
                const formatted = n.toLocaleString('en-IN', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: Math.max(0, precision),
                })
                const display = `${formatted} Cr`
                return (
                    <Typography
                        ref={textRef}
                        title={isOverflowed ? display : undefined}
                        variant="body2"
                        noWrap
                        sx={numericTextSx}
                        className={'font-feature-num'}
                    >
                        {display}
                    </Typography>
                )
            }
            case FieldType.BOOL:
                return <span ref={textRef} title={isOverflowed ? modifiedText : undefined} className={`${!abbreviate ? 'overview-value' : ''}`}>{modifiedText === 'true' ? 'Yes' : 'No'}</span>
            default: {
                const display = isNumericField && abbreviate ? formatLargeNumber(Number(modifiedText)) : modifiedText
                return <Typography
                    ref={textRef}
                    title={isOverflowed ? display : undefined}
                    variant="body2"
                    noWrap
                    sx={isNumericField ? numericTextSx : textSx}
                    className={'font-feature-num'}
                >
                    {display}
                </Typography>
            }
        }
    }

    return renderValue()
}

export default memo(OverviewValue)